import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Trash2, GraduationCap, Layers, Save, Check } from "lucide-react";
import { toast } from "sonner";

import PaymentStep from "@/components/PaymentStep";
import {
  ACCREDITATION_TYPE_LABEL,
  ACCREDITATION_TYPE_DESCRIPTION,
} from "@/lib/labels";

const ACCRED_FEE = { institutional: 699, programmatic: 499 };

const EMPTY_FORM = {
  name: "",
  legal_name: "",
  institution_type: "institution",
  accreditation_type: "institutional",
  programs: [],
  country: "",
  state: "",
  city: "",
  website: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  program_offerings: "",
  student_capacity: "",
  self_study_summary: "",
  declarations: { truth: false, standards: false },
};

export default function SchoolApplicationNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const resumeId = sp.get("draft");
  const [paid, setPaid] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const dirtyRef = useRef(false);

  // Resume draft if requested
  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/drafts/${resumeId}`);
        setDraftId(data.id);
        setForm({ ...EMPTY_FORM, ...(data.payload || {}) });
        setLastSavedAt(data.last_saved_at);
        toast.success("Draft restored");
      } catch (e) {
        toast.error(errMessage(e));
      }
    })();
  }, [resumeId]);

  const saveDraft = async (silent = false) => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/drafts", {
        track: "acis_school",
        payload: form,
        accreditation_type: form.accreditation_type,
        note: form.name || "Untitled accreditation draft",
      });
      setDraftId(data.id);
      setLastSavedAt(data.last_saved_at);
      dirtyRef.current = false;
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      if (!silent) toast.error(errMessage(e));
    } finally {
      setSavingDraft(false);
    }
  };

  // Auto-save every 60s if form has been touched
  useEffect(() => {
    const id = setInterval(() => {
      if (dirtyRef.current) saveDraft(true);
    }, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); dirtyRef.current = true; };
  const setDec = (k, v) => { setForm((f) => ({ ...f, declarations: { ...f.declarations, [k]: v } })); dirtyRef.current = true; };
  const addProgram = () => {
    setForm((f) => ({ ...f, programs: [...f.programs, { name: "", program_type: "", description: "", clock_hours: "", learning_objectives: "" }] }));
    dirtyRef.current = true;
  };
  const updateProgram = (i, k, v) => {
    setForm((f) => {
      const programs = [...f.programs];
      programs[i] = { ...programs[i], [k]: v };
      return { ...f, programs };
    });
    dirtyRef.current = true;
  };
  const deleteProgram = (i) => {
    setForm((f) => ({ ...f, programs: f.programs.filter((_, idx) => idx !== i) }));
    dirtyRef.current = true;
  };

  const headingByType = useMemo(
    () =>
      form.accreditation_type === "programmatic"
        ? "Programmatic Accreditation Application"
        : "Institutional Accreditation Application",
    [form.accreditation_type],
  );

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Institution name is required";
    if (!form.country.trim()) e.country = "Country is required";
    if (!form.contact_name.trim()) e.contact_name = "Primary contact name is required";
    if (!form.contact_email.trim()) e.contact_email = "Primary contact email is required";
    if (!form.self_study_summary.trim()) e.self_study_summary = "Self-study summary is required";
    if (!form.declarations.truth) e.dec_truth = "Required";
    if (!form.declarations.standards) e.dec_standards = "Required";
    if (form.accreditation_type === "programmatic") {
      if (form.programs.length === 0) e.programs = "Programmatic accreditation requires at least one program";
      form.programs.forEach((p, i) => {
        if (!p.name.trim()) e[`program_name_${i}`] = "Required";
        if (!p.program_type.trim()) e[`program_type_${i}`] = "Required";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!paid) {
      toast.error("Please complete the application fee before submitting.");
      return;
    }
    if (!validate()) {
      toast.error("Please correct the highlighted fields before submitting.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        programs: form.programs.map((p) => ({
          ...p,
          clock_hours: p.clock_hours ? Number(p.clock_hours) : null,
        })),
        program_offerings: form.program_offerings
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean),
        student_capacity: form.student_capacity ? Number(form.student_capacity) : null,
      };
      const { data } = await api.post("/schools/applications", payload);
      if (draftId) {
        try {
          await api.post(`/drafts/${draftId}/promote`);
        } catch { /* non-fatal */ }
      }
      toast.success("Accreditation application submitted");
      nav(`/dashboard/schools/${data.id}`);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-fade-up max-w-4xl" data-testid="school-app-new-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
            ACIS · New accreditation application
          </div>
          <h1
            className="font-heading text-3xl font-bold text-primary flex items-center gap-3"
            data-testid="sa-heading"
          >
            <Building2 className="h-7 w-7" /> {headingByType}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Application fee:{" "}
            <span className="font-semibold text-primary" data-testid="sa-fee-display">
              ${ACCRED_FEE[form.accreditation_type]} {form.accreditation_type === "programmatic" ? "(Programmatic)" : "(Institutional)"}
            </span>
            . All starred fields are required.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lastSavedAt && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[11px]" data-testid="sa-last-saved">
              <Check className="h-3 w-3 mr-1" /> Saved {new Date(lastSavedAt).toLocaleTimeString()}
            </Badge>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => saveDraft(false)}
            disabled={savingDraft}
            data-testid="sa-save-draft-btn"
          >
            <Save className="h-4 w-4 mr-1" /> {savingDraft ? "Saving…" : "Save draft"}
          </Button>
        </div>
      </div>

      <div className="mb-5">
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading text-lg font-semibold text-primary mb-1 flex items-center gap-2">
            <Layers className="h-5 w-5" /> Step 1 — Choose accreditation category
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Pick the category that matches the scope of your accreditation request.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {(["institutional", "programmatic"]).map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`sa-accred-type-${t}`}
                onClick={() => set("accreditation_type", t)}
                className={`text-left rounded-lg border p-4 transition ${
                  form.accreditation_type === t
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-heading font-semibold text-primary">
                    {ACCREDITATION_TYPE_LABEL[t]}
                  </span>
                  {form.accreditation_type === t && (
                    <Badge className="bg-primary text-white text-[10px]">Selected</Badge>
                  )}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {ACCREDITATION_TYPE_DESCRIPTION[t]}
                </p>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <div className="mb-5">
        <PaymentStep
          paymentType="acis_application_fee"
          entityType="school_application"
          amount={ACCRED_FEE[form.accreditation_type]}
          onPaid={() => setPaid(true)}
        />
      </div>

      <form onSubmit={submit} className={paid ? "" : "opacity-60 pointer-events-none select-none"}>
        <Card className="p-6 border-slate-200 mb-5">
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">Institution</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Institution name" required value={form.name} onChange={(v) => set("name", v)} testId="sa-name" error={errors.name} />
            <F label="Legal name" value={form.legal_name} onChange={(v) => set("legal_name", v)} testId="sa-legal" />
            <F label="Website" value={form.website} onChange={(v) => set("website", v)} testId="sa-website" />
            <F label="Country" required value={form.country} onChange={(v) => set("country", v)} testId="sa-country" error={errors.country} />
            <F label="State / Province" value={form.state} onChange={(v) => set("state", v)} testId="sa-state" />
            <F label="City" value={form.city} onChange={(v) => set("city", v)} testId="sa-city" />
            <F label="Student capacity" value={form.student_capacity} onChange={(v) => set("student_capacity", v)} testId="sa-capacity" type="number" />
          </div>
        </Card>

        <Card className="p-6 border-slate-200 mb-5">
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">Primary contact</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Contact name" required value={form.contact_name} onChange={(v) => set("contact_name", v)} testId="sa-contact-name" error={errors.contact_name} />
            <F label="Contact email" required type="email" value={form.contact_email} onChange={(v) => set("contact_email", v)} testId="sa-contact-email" error={errors.contact_email} />
            <F label="Contact phone" value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testId="sa-contact-phone" />
          </div>
        </Card>

        {form.accreditation_type === "programmatic" && (
          <Card className="p-6 border-slate-200 mb-5 bg-amber-50/30" data-testid="sa-programs-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold text-primary flex items-center gap-2">
                <GraduationCap className="h-5 w-5" /> Programs requesting accreditation
              </h2>
              <Button variant="outline" type="button" onClick={addProgram} data-testid="sa-add-program">
                <Plus className="h-4 w-4 mr-1" /> Add program
              </Button>
            </div>
            <p className="text-xs text-slate-600 mb-3">
              Only graduates of the listed program(s) will be eligible for IBTHM credentialing review.
            </p>
            {form.programs.length === 0 && (
              <div className={`rounded-md border-dashed border p-4 text-sm ${errors.programs ? "border-red-300 text-red-600 bg-red-50" : "border-slate-300 text-slate-500"}`}>
                {errors.programs || "Add at least one program to continue."}
              </div>
            )}
            <div className="space-y-4">
              {form.programs.map((p, i) => (
                <Card key={i} className="p-4 border-slate-200">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <F
                      label="Program name"
                      required
                      value={p.name}
                      onChange={(v) => updateProgram(i, "name", v)}
                      testId={`sa-program-name-${i}`}
                      error={errors[`program_name_${i}`]}
                    />
                    <F
                      label="Program type"
                      required
                      value={p.program_type}
                      onChange={(v) => updateProgram(i, "program_type", v)}
                      testId={`sa-program-type-${i}`}
                      error={errors[`program_type_${i}`]}
                      placeholder="e.g. Trichology, Holistic Medicine"
                    />
                    <F
                      label="Program clock hours"
                      value={p.clock_hours || ""}
                      onChange={(v) => updateProgram(i, "clock_hours", v.replace(/[^0-9]/g, ""))}
                      testId={`sa-program-hours-${i}`}
                      type="number"
                      placeholder="e.g. 600"
                    />
                    <div className="sm:col-span-1">
                      <Label className="mb-1.5 block">Program description</Label>
                      <Textarea
                        rows={2}
                        data-testid={`sa-program-desc-${i}`}
                        value={p.description}
                        onChange={(e) => updateProgram(i, "description", e.target.value)}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="mb-1.5 block">Learning objectives</Label>
                      <Textarea
                        rows={2}
                        data-testid={`sa-program-objectives-${i}`}
                        value={p.learning_objectives || ""}
                        onChange={(e) => updateProgram(i, "learning_objectives", e.target.value)}
                        placeholder="Bullet list of measurable graduate competencies"
                      />
                    </div>
                  </div>
                  <div className="mt-2 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => deleteProgram(i)}
                      data-testid={`sa-program-del-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        <Card className="p-6 border-slate-200 mb-5">
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">Self-study</h2>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Additional program offerings (comma-separated)</Label>
              <Input
                data-testid="sa-programs"
                placeholder="Clinical Nutrition, Trichology, Holistic Medicine"
                value={form.program_offerings}
                onChange={(e) => set("program_offerings", e.target.value)}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">
                Self-study summary <span className="text-red-500">*</span>
              </Label>
              <Textarea
                data-testid="sa-selfstudy"
                rows={6}
                value={form.self_study_summary}
                onChange={(e) => set("self_study_summary", e.target.value)}
                placeholder="Narrative addressing curriculum, faculty, clinical training, governance, finances, and outcomes data."
                className={errors.self_study_summary ? "border-red-300" : ""}
              />
              {errors.self_study_summary && (
                <div className="text-xs text-red-600 mt-1">{errors.self_study_summary}</div>
              )}
            </div>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 mb-5">
          <h2 className="font-heading text-lg font-semibold text-primary mb-4">Declarations</h2>
          <div className="space-y-3">
            <Dec
              testId="sa-dec-truth"
              checked={form.declarations.truth}
              onChange={(v) => setDec("truth", v)}
              title="Truthfulness"
              body="I attest that all information submitted is true and complete to the best of my knowledge."
              error={errors.dec_truth}
            />
            <Dec
              testId="sa-dec-standards"
              checked={form.declarations.standards}
              onChange={(v) => setDec("standards", v)}
              title="Compliance with standards"
              body="The institution agrees to comply with ACIS accreditation standards, on-site reviews, and ongoing audits."
              error={errors.dec_standards}
            />
          </div>
        </Card>

        <Button
          data-testid="sa-submit"
          type="submit"
          disabled={busy || !paid}
          className="bg-primary text-white hover:bg-[#113A64] h-11 px-6"
        >
          {busy
            ? "Submitting…"
            : !paid
            ? "Complete payment to submit"
            : `Submit ${headingByType}`}
        </Button>
      </form>
    </div>
  );
}

function F({ label, value, onChange, testId, type = "text", required, error, placeholder }) {
  return (
    <div>
      <Label className="mb-1.5 block">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={error ? "border-red-300" : ""}
      />
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

function Dec({ checked, onChange, title, body, testId, error }) {
  return (
    <label
      className={`flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-slate-50 ${
        error ? "border-red-300 bg-red-50/30" : "border-slate-200"
      }`}
    >
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <span>
        <span className="font-medium text-primary block text-sm">{title}</span>
        <span className="text-xs text-slate-600 leading-relaxed">{body}</span>
      </span>
    </label>
  );
}
